<template>
  <CAvatar v-if="isValidPicture" :size="size" :src="picture" />
  <CAvatar v-else :color="color" :size="size">
    <div :style="{ 'font-size': fontSize }">{{ initials }}</div>
  </CAvatar>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'

import { isImageAccessible } from '@lib/util/image'
import { stringToHslColor } from '@lib/util/color'
import CAvatar from '@lib/components/CAvatar/CAvatar.vue'

interface Props {
  picture?: string | null
  firstName?: string
  lastName?: string
  email?: string
  size?: number | 'small' | 'medium' | 'large'
  fontSize?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 36,
  fontSize: '14px',
})

const initials = computed(() => {
  if (props.firstName && props.lastName) {
    return (props.firstName.charAt(0) + props.lastName.charAt(0)).toLocaleUpperCase()
  }

  if (props.email) {
    const atIndex = props.email.indexOf('@')
    const lastDotIndex = props.email.lastIndexOf('.', atIndex)

    // Ensure both the dot and the "@" symbol are found and the dot is not the last character before the "@"
    if (lastDotIndex !== -1 && lastDotIndex + 1 < atIndex) {
      return (props.email.charAt(0) + props.email.charAt(lastDotIndex + 1)).toLocaleUpperCase()
    } else {
      return props.email.substring(0, 2).toLocaleUpperCase()
    }
  }

  return ''
})

const userNameEmail = computed(() =>
  props.firstName && props.lastName ? `${props.firstName} ${props.lastName}` : props.email,
)

const imageAccessible = ref(false)

const color = computed(() => stringToHslColor(userNameEmail.value ?? '', 55, 75))

const salesforceNoUserUrlPart = 'force.com/profilephoto/005/F'

const isValidPicture = computed(
  () => props.picture && imageAccessible.value && !props.picture.includes(salesforceNoUserUrlPart),
)

onMounted(() => {
  if (props.picture) {
    isImageAccessible(props.picture).catch(() => {
      imageAccessible.value = false
    })
  }
})
</script>
